import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, Link, useLocation, Navigate } from "react-router-dom";
import { UserButton } from "@clerk/react";
import { LayoutDashboard, Zap, Network, Wallet, ArrowUpRight, ShieldCheck, Receipt, Banknote, Users, Settings, Menu, X, PlusCircle, Package, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, REF_KEY } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { StatusBadge } from "@/components/common";

const MEMBER = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/activate", label: "Activation", icon: Zap },
  { to: "/dashboard/team", label: "My Team", icon: Network },
  { to: "/dashboard/earnings", label: "Earnings", icon: Wallet },
  { to: "/dashboard/add-money", label: "Add Money", icon: PlusCircle },
  { to: "/dashboard/shop", label: "Buy Products", icon: ShoppingBag },
  { to: "/dashboard/withdraw", label: "Withdraw", icon: ArrowUpRight },
];
const ADMIN = [
  { to: "/admin", label: "Admin Overview", icon: ShieldCheck, end: true },
  { to: "/admin/payments", label: "UTR Approvals", icon: Receipt },
  { to: "/admin/topups", label: "Top-ups", icon: PlusCircle },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: Banknote },
  { to: "/admin/members", label: "Members", icon: Users },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

const NavItem = ({ item, onClick }) => (
  <NavLink to={item.to} end={item.end} onClick={onClick}
    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
    className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-150 ${isActive ? "bg-emerald-500/10 text-emerald-400" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
    <item.icon className="h-4 w-4" />{item.label}
  </NavLink>
);

const Sidebar = ({ me, open, close }) => (
  <aside className={`fixed inset-y-0 left-0 z-40 w-64 border-r border-[#1F293D] bg-[#0B0F17] p-5 transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
    <div className="mb-8 flex items-center justify-between">
      <Link to="/dashboard" data-testid="sidebar-logo"><Logo /></Link>
      <button className="lg:hidden" onClick={close} data-testid="sidebar-close-btn"><X className="h-5 w-5 text-slate-400" /></button>
    </div>
    <p className="mb-2 px-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">Member</p>
    <nav className="space-y-1">{MEMBER.map((i) => <NavItem key={i.to} item={i} onClick={close} />)}</nav>
    {me?.user.role === "admin" && (
      <>
        <p className="mb-2 mt-8 px-3 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-500/80">Admin</p>
        <nav className="space-y-1">{ADMIN.map((i) => <NavItem key={i.to} item={i} onClick={close} />)}</nav>
      </>
    )}
  </aside>
);

export default function AppShell() {
  const api = useApi();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const attached = useRef(false);
  const { pathname } = useLocation();

  const refresh = useCallback(async () => {
    try {
      const data = await api("get", "/me");
      const ref = localStorage.getItem(REF_KEY);
      if (ref && !attached.current && !data.user.sponsor_id && data.user.status !== "active" && ref !== data.user.referral_code) {
        attached.current = true;
        try {
          const s = await api("post", "/me/sponsor", { code: ref });
          toast.success(`Linked to sponsor ${s.name}`);
          localStorage.removeItem(REF_KEY);
          return setMe(await api("get", "/me"));
        } catch { localStorage.removeItem(REF_KEY); }
      }
      setMe(data);
    } catch (e) { toast.error(errMsg(e)); }
  }, [api]);

  useEffect(() => { refresh(); }, [refresh]);

  if (me && pathname.startsWith("/admin") && me.user.role !== "admin") return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-200">
      <Sidebar me={me} open={open} close={() => setOpen(false)} />
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-[#1F293D] bg-[#0B0F17]/80 px-4 py-3 backdrop-blur-md sm:px-8">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-open-btn"><Menu className="h-5 w-5" /></button>
          <div className="hidden items-center gap-3 lg:flex">
            {me && <span className="text-sm text-slate-400">Hi, <span className="text-white" data-testid="header-user-name">{me.user.name}</span></span>}
          </div>
          <div className="flex items-center gap-4">
            {me && <StatusBadge status={me.user.status} testId="header-status-badge" />}
            <UserButton />
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-10">
          {me ? <Outlet context={{ me, refresh }} /> : <div className="py-24 text-center font-mono text-xs uppercase tracking-widest text-slate-500" data-testid="shell-loading">Loading…</div>}
        </main>
      </div>
    </div>
  );
}
